// © 2026 AttainIP Engineering
/**
 * Labels for the Select-Organisation picker (RFC-0510 W28).
 *
 * Identity fills a choice's `name`/`org_ext_id` in only when it can resolve the
 * organisation, so a choice may arrive carrying nothing but a UUID. A picker
 * cannot fail closed to showing nothing — an operator who cannot tell two rows
 * apart cannot sign in — so it degrades to an obviously-technical
 * "Organisation <first 8 of the id>" rather than presenting a bare UUID as if it
 * were a name.
 *
 * W29 adds what makes a PERSONAL organisation legible at all: it has no name and
 * no readable ext_id, so W28 could only offer it as "Organisation ce75cb74". It
 * is now labelled from `org_type`, which Identity reads off the organisation
 * record.
 *
 * ⚠️ THE TYPE IS READ, NEVER DEDUCED. "No name and no ext_id means personal" is
 * a guess that mislabels the first unnamed enterprise organisation. Only
 * `org_type === 'personal'` produces the personal label; a missing or
 * unrecognised type (the field is additive) falls back to W28's chain and is
 * assumed to be neither kind.
 *
 * Mirrors `attain-ui-web`'s copy of the same helper, the way the two SPAs
 * already keep separate copies of the Identity client.
 */

export interface OrgChoiceLike {
  organization_id: string;
  org_ext_id?: string | null;
  name?: string | null;
  /**
   * The organisation's own type from Identity's `OrgType` vocabulary
   * (`personal` | `domain` | `entra_tenant` | `enterprise_domain`). Additive
   * (RFC-0510 W29): absent means NOT KNOWN, never a kind. `string` on purpose,
   * so an unrecognised value degrades to the fallback label.
   */
  org_type?: string | null;
}

/** Identity's `OrgType.PERSONAL` — the one type with a label of its own. */
const PERSONAL_ORG_TYPE = 'personal';

/**
 * What an operator is choosing when they pick their own single-user
 * organisation. Deliberately NOT "selfserve" — that is our internal
 * `account_type` value and reads as a pricing tier, not as a kind of workspace.
 */
export const PERSONAL_ORG_LABEL = 'Personal workspace';

/** Is this choice a personal organisation? Only a POSITIVE match counts. */
export function isPersonalOrgChoice(choice: OrgChoiceLike): boolean {
  return choice.org_type === PERSONAL_ORG_TYPE;
}

/** A canonical UUID — `organization_id`, and the `ext_id` of entra-tenant orgs. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The readable half of an `ext_id` (`"enterprise-domain:acme.com"` →
 *  `"acme.com"`), or `null` when there isn't one. */
export function readableOrgExtId(extId?: string | null): string | null {
  const trimmed = extId?.trim();
  if (!trimmed) return null;
  const colon = trimmed.indexOf(':');
  const readable = (colon >= 0 ? trimmed.slice(colon + 1) : trimmed).trim();
  if (!readable || UUID_RE.test(readable)) return null;
  return readable;
}

/** The primary label for a choice. Always non-empty. */
export function orgChoiceLabel(choice: OrgChoiceLike): string {
  // The type first: it is the only thing actually KNOWN about a personal org.
  if (isPersonalOrgChoice(choice)) return PERSONAL_ORG_LABEL;

  const name = choice.name?.trim();
  if (name && !UUID_RE.test(name)) return name;

  const readable = readableOrgExtId(choice.org_ext_id);
  if (readable) return readable;

  return `Organisation ${choice.organization_id.slice(0, 8)}`;
}

/** A secondary line, or `null` when it would only repeat the primary. */
export function orgChoiceSubLabel(choice: OrgChoiceLike): string | null {
  // A personal organisation has no domain, and `personal:<uuid>` is an
  // identifier rather than a second line worth reading.
  if (isPersonalOrgChoice(choice)) return null;

  const readable = readableOrgExtId(choice.org_ext_id);
  if (!readable) return null;
  return readable === orgChoiceLabel(choice) ? null : readable;
}
